import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Bell,
  CheckCircle,
  Clock,
  Cpu,
  Gauge,
  HardDrive,
  RefreshCw,
  Search,
  Server,
  Settings,
  Shield,
  Terminal,
  XCircle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  getAlerts,
  getIncident,
  getIncidents,
  getMonitoringConfig,
  getServiceLogs,
  getSystemHealthOverview,
  getSystemHealthServices,
  getSystemMetrics,
  getOperation,
  submitServiceAction,
  testAlert,
  updateMonitoringConfig,
} from '../../Services/SystemHealthService';
import { getActiveEmailSenderProfiles } from '../../Services/EmailAdminService';
import {
  Alert,
  Incident,
  LogEntry,
  MonitoringConfig,
  ServiceAction,
  ServiceStatus,
  SystemMetrics,
} from '../../Types/SystemHealthType';
import { EmailSenderProfile } from '../../Types/EmailAdminType';

type TabKey = 'overview' | 'services' | 'logs' | 'incidents' | 'vps' | 'settings';
type LogRange = 'all' | '4h' | '6h' | '24h' | 'today' | 'custom';
type IncidentFilter = 'ALL' | 'ACTIVE' | 'RESOLVED';
type Notice = { type: 'success' | 'error'; text: string };
type NumericConfigKey =
  | 'cpuWarning'
  | 'cpuCritical'
  | 'memoryWarning'
  | 'memoryCritical'
  | 'diskWarning'
  | 'diskCritical'
  | 'consecutiveFailureThreshold'
  | 'recoveryThreshold'
  | 'pollingIntervalSeconds'
  | 'healthTimeoutSeconds'
  | 'incidentCooldownSeconds';

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'overview', label: 'Overview', icon: <Activity size={16} /> },
  { key: 'services', label: 'Services', icon: <Server size={16} /> },
  { key: 'logs', label: 'Logs & Diagnostics', icon: <Terminal size={16} /> },
  { key: 'incidents', label: 'Incidents & Alerts', icon: <AlertTriangle size={16} /> },
  { key: 'vps', label: 'VPS Configuration', icon: <Shield size={16} /> },
  { key: 'settings', label: 'Monitoring Settings', icon: <Settings size={16} /> },
];

const LOG_LINE_OPTIONS = [200, 500, 1000, 2000];
const LOG_RANGES: { key: LogRange; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: '4h', label: 'Last 4h' },
  { key: '6h', label: 'Last 6h' },
  { key: '24h', label: 'Last 24h' },
  { key: 'today', label: 'Today' },
  { key: 'custom', label: 'Custom' },
];
const SEVERITIES = ['', 'ERROR', 'WARN', 'INFO', 'DEBUG'];

const NUMERIC_FIELDS: { key: NumericConfigKey; label: string; help: string }[] = [
  { key: 'cpuWarning', label: 'CPU warning %', help: 'Logged as a warning in the agent log. There is no CPU email alert type.' },
  { key: 'cpuCritical', label: 'CPU critical %', help: 'Writes a critical entry to the agent log when exceeded.' },
  { key: 'memoryWarning', label: 'Memory warning %', help: 'Informational threshold.' },
  { key: 'memoryCritical', label: 'Memory critical %', help: 'Sends a MEMORY_PRESSURE alert (cooldown applies).' },
  { key: 'diskWarning', label: 'Disk warning %', help: 'Informational threshold.' },
  { key: 'diskCritical', label: 'Disk critical %', help: 'Sends a DISK_FULL alert (cooldown applies).' },
  { key: 'consecutiveFailureThreshold', label: 'Failure threshold', help: 'Failed polls required before an incident is opened and BACKEND_DOWN/DATABASE_DOWN is sent.' },
  { key: 'recoveryThreshold', label: 'Recovery threshold', help: 'Consecutive healthy polls required to resolve an incident and send SERVICE_RECOVERY.' },
  { key: 'pollingIntervalSeconds', label: 'Poll interval (s)', help: 'Service and metric polling frequency. Applied immediately on the agent.' },
  { key: 'healthTimeoutSeconds', label: 'Health timeout (s)', help: 'HTTP/TCP connect and read timeout for health checks.' },
  { key: 'incidentCooldownSeconds', label: 'Alert cooldown (s)', help: 'Minimum time between two alerts of the same type for the same service (prevents alert storms).' },
];

const ALERT_TYPES = [
  { value: 'BACKEND_DOWN', label: 'BACKEND_DOWN', help: 'A service is DOWN or DEGRADED past the failure threshold.' },
  { value: 'DATABASE_DOWN', label: 'DATABASE_DOWN', help: 'MySQL (database) is down.' },
  { value: 'DISK_FULL', label: 'DISK_FULL', help: 'Disk usage crossed the critical threshold.' },
  { value: 'MEMORY_PRESSURE', label: 'MEMORY_PRESSURE', help: 'Memory usage crossed the critical threshold.' },
  { value: 'FAILED_RESTART', label: 'FAILED_RESTART', help: 'A START/STOP/RESTART action failed or timed out.' },
  { value: 'REPEATED_FAILURES', label: 'REPEATED_FAILURES', help: 'A service keeps failing (3x the failure threshold).' },
  { value: 'SERVICE_RECOVERY', label: 'SERVICE_RECOVERY', help: 'A service recovered and its incident was resolved.' },
];

const isOpenStatus = (status?: string) => !!status && status.toUpperCase() !== 'RESOLVED';

const TIME_OPTIONS: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true };
const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true };

const formatDateTime = (value?: string) => (value ? new Date(value).toLocaleString(undefined, DATE_TIME_OPTIONS) : '-');
const formatTime = (value?: string) => (value ? new Date(value).toLocaleTimeString(undefined, TIME_OPTIONS) : '-');

const pad = (value: number) => String(value).padStart(2, '0');

const toLocalInput = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;

const toServerDateTime = (value: string) => (value.length === 16 ? `${value}:00` : value);

export default function SystemHealthComponent() {
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [config, setConfig] = useState<MonitoringConfig | null>(null);
  const [configDirty, setConfigDirty] = useState(false);
  const configDirtyRef = useRef(false);
  const [senderProfiles, setSenderProfiles] = useState<EmailSenderProfile[]>([]);
  const [lastRefreshed, setLastRefreshed] = useState<string | null>(null);

  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsFetchedAt, setLogsFetchedAt] = useState<string | null>(null);
  const [logLines, setLogLines] = useState(200);
  const [logSeverity, setLogSeverity] = useState('');
  const [logSearch, setLogSearch] = useState('');
  const [logRange, setLogRange] = useState<LogRange>('all');
  const [logFrom, setLogFrom] = useState('');
  const [logTo, setLogTo] = useState('');
  const [logSort, setLogSort] = useState<'desc' | 'asc'>('desc');

  const [incidentFilter, setIncidentFilter] = useState<IncidentFilter>('ALL');
  const [incidentService, setIncidentService] = useState('');
  const [incidentSearch, setIncidentSearch] = useState('');
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [incidentDetailLoading, setIncidentDetailLoading] = useState(false);

  const [pendingOperations, setPendingOperations] = useState<Record<string, string>>({});

  const fetchAlerts = async () => {
    const res = await getAlerts();
    if (res.success && Array.isArray(res.data)) {
      setAlerts(res.data);
    }
  };

  const fetchSenderProfiles = async () => {
    const res = await getActiveEmailSenderProfiles();
    if (res.success && Array.isArray(res.data)) {
      setSenderProfiles(res.data);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewRes, servicesRes, incidentsRes, metricsRes, configRes] = await Promise.all([
        getSystemHealthOverview(),
        getSystemHealthServices(),
        getIncidents(),
        getSystemMetrics(),
        getMonitoringConfig(),
      ]);

      const failures = [
        ['overview', overviewRes],
        ['services', servicesRes],
        ['incidents', incidentsRes],
        ['metrics', metricsRes],
        ['config', configRes],
      ] as const;
      for (const [name, res] of failures) {
        if (!res?.success) {
          throw new Error(res?.message || `Failed to load ${name}`);
        }
      }
      if (!Array.isArray(servicesRes.data)) {
        throw new Error('Unexpected services payload from server');
      }
      if (!Array.isArray(incidentsRes.data)) {
        throw new Error('Unexpected incidents payload from server');
      }

      setServices(servicesRes.data);
      setMetrics(metricsRes.data);
      setIncidents(incidentsRes.data);
      if (!configDirtyRef.current) {
        setConfig(configRes.data);
      }
      setLastRefreshed(overviewRes.data.lastRefreshed || new Date().toISOString());
      fetchAlerts();
    } catch (err: any) {
      setError(err?.message || 'Unable to reach the operations agent.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    fetchSenderProfiles();
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!selectedServiceId && services.length > 0) {
      setSelectedServiceId(services[0].serviceId);
    }
  }, [services, selectedServiceId]);

  useEffect(() => {
    const timers: number[] = [];
    Object.entries(pendingOperations).forEach(([serviceId, operationId]) => {
      const timer = window.setInterval(async () => {
        const res = await getOperation(operationId);
        if (res.success && (res.data.status === 'SUCCEEDED' || res.data.status === 'FAILED' || res.data.status === 'TIMED_OUT')) {
          setPendingOperations((prev) => {
            const next = { ...prev };
            delete next[serviceId];
            return next;
          });
          fetchAll();
        }
      }, 3000);
      timers.push(timer);
    });
    return () => timers.forEach((t) => clearInterval(t));
  }, [pendingOperations]);

  const activeIncidents = useMemo(() => incidents.filter((i) => isOpenStatus(i.status)), [incidents]);
  const resolvedIncidents = useMemo(() => incidents.filter((i) => !isOpenStatus(i.status)), [incidents]);

  const filteredIncidents = useMemo(() => {
    const search = incidentSearch.trim().toLowerCase();
    return incidents
      .filter((i) => {
        if (incidentFilter === 'ACTIVE') return isOpenStatus(i.status);
        if (incidentFilter === 'RESOLVED') return !isOpenStatus(i.status);
        return true;
      })
      .filter((i) => !incidentService || i.serviceId === incidentService)
      .filter((i) => {
        if (!search) return true;
        return [i.serviceId, i.alertType, i.message, i.status]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(search));
      })
      .sort((a, b) => {
        const left = a.startedAt ? new Date(a.startedAt).getTime() : 0;
        const right = b.startedAt ? new Date(b.startedAt).getTime() : 0;
        return right - left;
      });
  }, [incidents, incidentFilter, incidentService, incidentSearch]);

  const handleAction = async (serviceId: string, action: ServiceAction) => {
    setError(null);
    const res = await submitServiceAction(serviceId, { action });
    if (!res.success) {
      setError(res.message || `Failed to ${action} ${serviceId}`);
      return;
    }
    setPendingOperations((prev) => ({ ...prev, [serviceId]: res.data.operationId }));
  };

  const resolveSince = (): string | undefined => {
    const now = new Date();
    switch (logRange) {
      case '4h':
        return toServerDateTime(toLocalInput(new Date(now.getTime() - 4 * 3600 * 1000)));
      case '6h':
        return toServerDateTime(toLocalInput(new Date(now.getTime() - 6 * 3600 * 1000)));
      case '24h':
        return toServerDateTime(toLocalInput(new Date(now.getTime() - 24 * 3600 * 1000)));
      case 'today': {
        const start = new Date(now);
        start.setHours(0, 0, 0, 0);
        return toServerDateTime(toLocalInput(start));
      }
      case 'custom':
        return logFrom ? toServerDateTime(logFrom) : undefined;
      default:
        return undefined;
    }
  };

  const handleFetchLogs = async () => {
    if (!selectedServiceId) return;
    setLogsLoading(true);
    setLogs([]);
    setError(null);
    const res = await getServiceLogs(
      selectedServiceId,
      logLines,
      resolveSince(),
      logSeverity || undefined,
      logSearch || undefined
    );
    if (res.success && Array.isArray(res.data)) {
      const until = logTo ? new Date(toServerDateTime(logTo)).getTime() : null;
      const filtered = until
        ? res.data.filter((entry) => !entry.timestamp || new Date(entry.timestamp).getTime() <= until)
        : [...res.data];
      filtered.sort((a, b) => {
        const left = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const right = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return logSort === 'asc' ? left - right : right - left;
      });
      setLogs(filtered);
      setLogsFetchedAt(new Date().toISOString());
    } else {
      setError(res.message || 'Failed to load logs');
    }
    setLogsLoading(false);
  };

  const handleIncidentClick = async (incident: Incident) => {
    setIncidentDetailLoading(true);
    const res = await getIncident(incident.incidentId);
    if (res.success) {
      setSelectedIncident(res.data);
    } else {
      setError(res.message || 'Failed to load incident details');
    }
    setIncidentDetailLoading(false);
  };

  const updateConfigField = (key: NumericConfigKey, value: number) => {
    if (!config) return;
    setConfig({ ...config, [key]: value });
    configDirtyRef.current = true;
    setConfigDirty(true);
    setNotice(null);
  };

  const toggleAlertType = (value: string) => {
    if (!config) return;
    const current = config.enabledAlertTypes || [];
    const next = current.includes(value) ? current.filter((type) => type !== value) : [...current, value];
    setConfig({ ...config, enabledAlertTypes: next });
    configDirtyRef.current = true;
    setConfigDirty(true);
    setNotice(null);
  };

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!config) return;
    setError(null);
    setNotice(null);
    const res = await updateMonitoringConfig(config);
    if (!res.success) {
      setError(res.message || 'Failed to update monitoring config');
      setNotice({ type: 'error', text: res.message || 'Failed to save monitoring settings.' });
    } else {
      setConfig(res.data);
      configDirtyRef.current = false;
      setConfigDirty(false);
      setNotice({ type: 'success', text: `Settings saved and pushed to the agent at ${formatTime(new Date().toISOString())}.` });
    }
  };

  const handleTestAlert = async () => {
    setError(null);
    setNotice(null);
    const res = await testAlert();
    if (!res.success) {
      setError(res.message || 'Failed to send test alert');
      setNotice({ type: 'error', text: res.message || 'Failed to queue the test alert.' });
    } else {
      const sent = res.data?.sent ?? 0;
      const failed = res.data?.failed ?? 0;
      setNotice({
        type: failed > 0 ? 'error' : 'success',
        text: failed > 0
          ? `Test alert: ${sent} delivered, ${failed} failed. Check the email service configuration.`
          : `Test alert delivered to ${sent} recipient${sent === 1 ? '' : 's'}.`,
      });
    }
  };

  const formatDuration = (seconds?: number) => {
    if (seconds === undefined || seconds === null) return '-';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}h ${m}m ${s}s`;
  };

  const formatIncidentDuration = (incident: Incident) => {
    if (!incident.startedAt) return '-';
    const start = new Date(incident.startedAt).getTime();
    const end = incident.resolvedAt ? new Date(incident.resolvedAt).getTime() : Date.now();
    const seconds = Math.max(0, Math.floor((end - start) / 1000));
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m`;
    return `${seconds}s`;
  };

  const statusBadge = (status?: string) => {
    const base = 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide';
    switch (status) {
      case 'HEALTHY':
      case 'RESOLVED':
      case 'SUCCEEDED':
      case 'SENT':
        return <span className={`${base} border border-emerald-500/30 bg-emerald-500/10 text-emerald-400`}><CheckCircle size={12} /> {status}</span>;
      case 'DEGRADED':
      case 'ACKNOWLEDGED':
      case 'RUNNING':
      case 'WAITING_FOR_HEALTH_CHECK':
      case 'PENDING':
        return <span className={`${base} border border-amber-500/30 bg-amber-500/10 text-amber-400`}><AlertTriangle size={12} /> {status}</span>;
      case 'DOWN':
      case 'OPEN':
      case 'ACTIVE':
      case 'FAILED':
      case 'TIMED_OUT':
        return <span className={`${base} border border-rose-500/30 bg-rose-500/10 text-rose-400`}><XCircle size={12} /> {status}</span>;
      default:
        return <span className={`${base} border border-slate-600 bg-slate-800 text-slate-400`}>{status || 'UNKNOWN'}</span>;
    }
  };

  const severityClass = (severity?: string) => {
    switch (severity?.toUpperCase()) {
      case 'ERROR':
      case 'FATAL':
        return 'text-rose-400';
      case 'WARN':
      case 'WARNING':
        return 'text-amber-400';
      case 'DEBUG':
        return 'text-slate-500';
      default:
        return 'text-slate-400';
    }
  };

  const metricCard = (icon: React.ReactNode, label: string, value: string, sub?: string, color?: string) => (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
      <div className="mb-2 flex items-center gap-2 text-slate-400">
        <span className={color || 'text-cyan-400'}>{icon}</span>
        <span className="text-xs font-bold uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-2xl font-black text-slate-100">{value}</div>
      {sub && <div className="mt-1 text-[11px] font-semibold text-slate-500">{sub}</div>}
    </div>
  );

  const filterChip = (active: boolean, label: string, onClick: () => void) => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      className={`rounded-lg border px-3 py-1.5 text-[11px] font-bold transition ${
        active
          ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-400'
          : 'border-slate-800 bg-slate-900 text-slate-400 hover:border-slate-700 hover:text-slate-200'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <Activity size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">System Health</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">VPS services, metrics, logs and incidents</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {lastRefreshed && (
            <span className="text-[11px] font-semibold text-slate-500">
              Refreshed {formatTime(lastRefreshed)}
            </span>
          )}
          <button
            type="button"
            onClick={fetchAll}
            disabled={loading}
            className="flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-bold text-slate-200 hover:border-cyan-500/40 hover:text-cyan-400 disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm font-semibold text-rose-400">
          {error}
        </div>
      )}

      {notice && (
        <div
          className={`mb-4 rounded-xl border px-4 py-3 text-sm font-semibold ${
            notice.type === 'success'
              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
              : 'border-rose-500/30 bg-rose-500/10 text-rose-400'
          }`}
        >
          {notice.text}
        </div>
      )}

      <div className="mb-6 flex flex-wrap gap-2">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold transition ${
              activeTab === tab.key
                ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-400'
                : 'border-slate-800 bg-slate-900/60 text-slate-400 hover:border-slate-700 hover:text-slate-200'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {metricCard(<Cpu size={18} />, 'CPU', `${metrics?.cpuPercent?.toFixed(1) ?? '-'}%`, undefined, 'text-cyan-400')}
                {metricCard(<HardDrive size={18} />, 'Memory', `${metrics?.memoryUsedPercent?.toFixed(1) ?? '-'}%`, `${metrics?.memoryUsedMb ?? '-'} / ${metrics?.memoryTotalMb ?? '-'} MB`, 'text-violet-400')}
                {metricCard(<Gauge size={18} />, 'Disk', `${metrics?.diskUsedPercent?.toFixed(1) ?? '-'}%`, `${metrics?.diskUsedGb ?? '-'} / ${metrics?.diskTotalGb ?? '-'} GB`, 'text-emerald-400')}
                {metricCard(<Activity size={18} />, 'Load', `${metrics?.loadAverage1?.toFixed(2) ?? '-'}`, `1m / 5m / 15m: ${metrics?.loadAverage1?.toFixed(2) ?? '-'} / ${metrics?.loadAverage5?.toFixed(2) ?? '-'} / ${metrics?.loadAverage15?.toFixed(2) ?? '-'}`, 'text-amber-400')}
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 lg:col-span-2">
                  <h3 className="mb-3 text-sm font-black text-slate-100">Service Status</h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {services.map((s) => (
                      <div key={s.serviceId} className="rounded-xl border border-slate-800 bg-slate-900/80 p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-slate-200">{s.displayName}</span>
                          {statusBadge(s.status)}
                        </div>
                        <div className="mt-2 text-[11px] font-semibold text-slate-500">
                          {s.subStatus || '-'} · Uptime {formatDuration(s.uptimeSeconds)} · Restart {s.restartCount ?? 0}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                  <h3 className="mb-3 text-sm font-black text-slate-100">Incidents</h3>
                  <div className="flex items-center gap-3">
                    <span className="text-4xl font-black text-rose-400">{activeIncidents.length}</span>
                    <span className="text-xs font-semibold text-slate-400">active incidents</span>
                  </div>
                  <div className="mt-3 space-y-2">
                    {incidents.slice(0, 5).map((i) => (
                      <div key={i.incidentId} className="rounded-xl border border-slate-800 bg-slate-900/80 p-2 text-[11px]">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-slate-300">{i.serviceId}</span>
                          {statusBadge(i.status)}
                        </div>
                        <div className="mt-1 text-slate-500 truncate">{i.message || i.alertType}</div>
                      </div>
                    ))}
                    {incidents.length === 0 && <div className="text-[11px] text-slate-500">No incidents recorded.</div>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
              <h3 className="mb-3 text-sm font-black text-slate-100">Services</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="border-b border-slate-800 text-slate-400">
                    <tr>
                      <th className="py-2 pr-4 font-bold">Service</th>
                      <th className="py-2 pr-4 font-bold">Status</th>
                      <th className="py-2 pr-4 font-bold">Sub-status</th>
                      <th className="py-2 pr-4 font-bold">Health</th>
                      <th className="py-2 pr-4 font-bold">Uptime</th>
                      <th className="py-2 pr-4 font-bold">Response</th>
                      <th className="py-2 pr-4 font-bold">Restarts</th>
                      <th className="py-2 font-bold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((s) => (
                      <tr key={s.serviceId} className="border-b border-slate-800/50 last:border-0">
                        <td className="py-3 pr-4">
                          <div className="font-bold text-slate-200">{s.displayName}</div>
                          <div className="text-[11px] text-slate-500">{s.systemdUnit}</div>
                        </td>
                        <td className="py-3 pr-4">{statusBadge(s.status)}</td>
                        <td className="py-3 pr-4 text-slate-400">{s.subStatus || '-'}</td>
                        <td className="py-3 pr-4">
                          {s.healthOk ? (
                            <span className="inline-flex items-center gap-1 text-emerald-400"><CheckCircle size={12} /> OK</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-rose-400"><XCircle size={12} /> Fail</span>
                          )}
                        </td>
                        <td className="py-3 pr-4 text-slate-400">{formatDuration(s.uptimeSeconds)}</td>
                        <td className="py-3 pr-4 text-slate-400">{s.responseTimeMs ? `${s.responseTimeMs}ms` : '-'}</td>
                        <td className="py-3 pr-4 text-slate-400">{s.restartCount ?? 0}</td>
                        <td className="py-3">
                          <div className="flex flex-wrap gap-2">
                            {(['START', 'STOP', 'RESTART'] as ServiceAction[]).map((action) => {
                              const effective = s.effectiveActions ?? s.permittedActions ?? [];
                              const allowed = s.actionsEnabled && effective.includes(action);
                              const restricted =
                                s.permittedActions?.includes(action) && !effective.includes(action);
                              const pending = pendingOperations[s.serviceId];
                              const restrictedReason =
                                action === 'STOP'
                                  ? 'STOP is blocked by policy: it is either the environment serving this dashboard, or a production service requiring the SYSTEM_HEALTH_PROD_STOP privilege.'
                                  : 'This action is restricted by policy.';
                              return (
                                <button
                                  key={action}
                                  type="button"
                                  disabled={!allowed || !!pending}
                                  title={restricted ? restrictedReason : undefined}
                                  onClick={() => handleAction(s.serviceId, action)}
                                  className={`rounded-lg px-2 py-1 text-[11px] font-bold ${
                                    allowed && !pending
                                      ? 'border border-slate-700 bg-slate-800 text-slate-200 hover:border-cyan-500/40 hover:text-cyan-400'
                                      : restricted
                                        ? 'cursor-not-allowed border border-amber-500/20 bg-amber-500/5 text-amber-500/70'
                                        : 'cursor-not-allowed border border-slate-800 bg-slate-900/50 text-slate-600'
                                  }`}
                                >
                                  {pending ? '...' : action}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'logs' && (
            <div className="space-y-4">
              <div className="space-y-3 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="mb-1 block text-[11px] font-bold text-slate-400">Service</label>
                    <select
                      value={selectedServiceId}
                      onChange={(e) => setSelectedServiceId(e.target.value)}
                      className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-200 outline-none focus:border-cyan-500/40"
                    >
                      {services.map((s) => (
                        <option key={s.serviceId} value={s.serviceId}>{s.displayName}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold text-slate-400">Lines</label>
                    <select
                      value={logLines}
                      onChange={(e) => setLogLines(Number(e.target.value))}
                      className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-200 outline-none focus:border-cyan-500/40"
                    >
                      {LOG_LINE_OPTIONS.map((count) => (
                        <option key={count} value={count}>{count}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold text-slate-400">Severity</label>
                    <select
                      value={logSeverity}
                      onChange={(e) => setLogSeverity(e.target.value)}
                      className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-200 outline-none focus:border-cyan-500/40"
                    >
                      {SEVERITIES.map((severity) => (
                        <option key={severity || 'any'} value={severity}>{severity || 'Any'}</option>
                      ))}
                    </select>
                  </div>
                  <div className="min-w-[180px] flex-1">
                    <label className="mb-1 block text-[11px] font-bold text-slate-400">Search</label>
                    <div className="flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 focus-within:border-cyan-500/40">
                      <Search size={14} className="text-slate-500" />
                      <input
                        type="text"
                        value={logSearch}
                        onChange={(e) => setLogSearch(e.target.value)}
                        placeholder="Filter text"
                        className="w-full bg-transparent text-xs text-slate-200 outline-none placeholder:text-slate-600"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleFetchLogs}
                    disabled={logsLoading || !selectedServiceId}
                    className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-4 py-2 text-xs font-bold text-cyan-400 hover:bg-cyan-500/20 disabled:opacity-50"
                  >
                    {logsLoading ? 'Loading...' : 'Fetch Logs'}
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-2 border-t border-slate-800 pt-3">
                  <span className="mr-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">Range</span>
                  {LOG_RANGES.map((range) =>
                    filterChip(logRange === range.key, range.label, () => setLogRange(range.key))
                  )}
                  {logRange === 'custom' && (
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="datetime-local"
                        value={logFrom}
                        onChange={(e) => setLogFrom(e.target.value)}
                        className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-1.5 text-[11px] text-slate-200 outline-none focus:border-cyan-500/40"
                      />
                      <span className="text-[11px] text-slate-500">to</span>
                      <input
                        type="datetime-local"
                        value={logTo}
                        onChange={(e) => setLogTo(e.target.value)}
                        className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-1.5 text-[11px] text-slate-200 outline-none focus:border-cyan-500/40"
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setLogSort((prev) => (prev === 'desc' ? 'asc' : 'desc'))}
                    className="ml-auto rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-[11px] font-bold text-slate-300 hover:border-cyan-500/40 hover:text-cyan-400"
                  >
                    {logSort === 'desc' ? 'Newest first' : 'Oldest first'}
                  </button>
                  <span className="text-[11px] font-semibold text-slate-500">
                    {logs.length} entries{logsFetchedAt ? ` · fetched ${formatTime(logsFetchedAt)}` : ''}
                  </span>
                </div>
              </div>

              <div className="max-h-[60vh] overflow-y-auto rounded-2xl border border-slate-800 bg-slate-950 p-4 font-mono text-xs">
                {logs.length === 0 && !logsLoading && <div className="text-slate-500">No logs to display. Choose filters and press Fetch Logs.</div>}
                {logs.map((log, idx) => (
                  <div key={idx} className="mb-2 border-b border-slate-900 pb-2 last:border-0">
                    <div className="flex items-center gap-2 text-[11px] text-slate-500">
                      <span className="text-slate-400">{log.timestamp ? formatDateTime(log.timestamp) : '-'}</span>
                      <span className="rounded bg-slate-900 px-1 py-0.5 text-slate-400">{log.source}</span>
                      <span className={`font-bold ${severityClass(log.severity)}`}>{log.severity}</span>
                    </div>
                    <div className="mt-0.5 whitespace-pre-wrap text-slate-300">{log.message}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'incidents' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {metricCard(<AlertTriangle size={18} />, 'Active', String(activeIncidents.length), 'Current incidents', 'text-rose-400')}
                {metricCard(<CheckCircle size={18} />, 'Resolved', String(resolvedIncidents.length), 'Total resolved', 'text-emerald-400')}
                {metricCard(<Bell size={18} />, 'Alerts sent', String(alerts.filter((a) => a.status === 'SENT').length), `${alerts.filter((a) => a.status === 'FAILED').length} failed`, 'text-cyan-400')}
                {metricCard(<Clock size={18} />, 'Total incidents', String(incidents.length), 'All time', 'text-violet-400')}
              </div>

              <div className="space-y-3 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  {(['ALL', 'ACTIVE', 'RESOLVED'] as IncidentFilter[]).map((filter) =>
                    filterChip(incidentFilter === filter, filter, () => setIncidentFilter(filter))
                  )}
                  <select
                    value={incidentService}
                    onChange={(e) => setIncidentService(e.target.value)}
                    className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-[11px] font-bold text-slate-300 outline-none focus:border-cyan-500/40"
                  >
                    <option value="">All services</option>
                    {services.map((s) => (
                      <option key={s.serviceId} value={s.serviceId}>{s.displayName}</option>
                    ))}
                  </select>
                  <div className="flex min-w-[200px] flex-1 items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 focus-within:border-cyan-500/40">
                    <Search size={13} className="text-slate-500" />
                    <input
                      type="text"
                      value={incidentSearch}
                      onChange={(e) => setIncidentSearch(e.target.value)}
                      placeholder="Search service, type or message"
                      className="w-full bg-transparent text-[11px] text-slate-200 outline-none placeholder:text-slate-600"
                    />
                  </div>
                  <span className="text-[11px] font-semibold text-slate-500">{filteredIncidents.length} shown</span>
                </div>

                <div className="space-y-2">
                  {filteredIncidents.map((i) => (
                    <button
                      key={i.incidentId}
                      type="button"
                      onClick={() => handleIncidentClick(i)}
                      className="flex w-full flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-800 bg-slate-900/80 p-3 text-left hover:border-cyan-500/30"
                    >
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-slate-200">
                          {i.serviceId} · {i.alertType || 'Incident'}
                        </div>
                        <div className="truncate text-[11px] text-slate-500">
                          {i.message || 'No message'} · started {i.startedAt ? formatDateTime(i.startedAt) : '-'}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[11px] font-semibold text-slate-500">
                          {isOpenStatus(i.status) ? 'Ongoing' : 'Took'} {formatIncidentDuration(i)}
                        </span>
                        {statusBadge(i.status)}
                      </div>
                    </button>
                  ))}
                  {filteredIncidents.length === 0 && (
                    <div className="text-xs text-slate-500">No incidents match the current filters.</div>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-black text-slate-100">Alert Delivery History</h3>
                    <p className="text-[11px] font-semibold text-slate-500">Automated alert emails delivered through the application email service, including failures.</p>
                  </div>
                  <button
                    type="button"
                    onClick={fetchAlerts}
                    className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-[11px] font-bold text-slate-300 hover:border-cyan-500/40 hover:text-cyan-400"
                  >
                    <RefreshCw size={12} /> Refresh
                  </button>
                </div>
                <div className="max-h-[40vh] space-y-2 overflow-y-auto">
                  {alerts.map((alert) => (
                    <div key={alert.alertId} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-800 bg-slate-900/80 p-3">
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-slate-200">
                          {alert.alertType} · {alert.serviceId || '-'}
                        </div>
                        <div className="truncate text-[11px] text-slate-500">
                          {alert.recipient || 'no recipient'} · {alert.subject || 'no subject'}
                          {alert.errorMessage ? ` · ${alert.errorMessage}` : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[11px] font-semibold text-slate-500">
                          {alert.sentAt ? formatDateTime(alert.sentAt) : '-'}
                        </span>
                        {statusBadge(alert.status)}
                      </div>
                    </div>
                  ))}
                  {alerts.length === 0 && (
                    <div className="text-xs text-slate-500">
                      No automated alerts sent yet. Configure recipients and enabled types under Monitoring Settings, then use Test Alert.
                    </div>
                  )}
                </div>
              </div>

              {selectedIncident && (
                <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-black text-slate-100">Incident Timeline</h3>
                      <p className="text-[11px] font-semibold text-slate-500">
                        {selectedIncident.serviceId} · {selectedIncident.alertType || 'Incident'} · {formatIncidentDuration(selectedIncident)}
                      </p>
                    </div>
                    <button type="button" onClick={() => setSelectedIncident(null)} className="text-xs text-slate-400 hover:text-slate-200">Close</button>
                  </div>
                  {incidentDetailLoading ? (
                    <div className="text-xs text-slate-500">Loading events...</div>
                  ) : (
                    <div className="space-y-2">
                      {selectedIncident.events?.map((e) => (
                        <div key={e.eventId} className="rounded-xl border border-slate-800 bg-slate-900/80 p-2 text-xs">
                          <div className="flex items-center gap-2 text-[11px] text-slate-500">
                            <span>{e.createdAt ? formatDateTime(e.createdAt) : '-'}</span>
                            <span className={`font-bold ${e.severity === 'RESOLVED' ? 'text-emerald-400' : e.severity === 'CREATED' ? 'text-rose-400' : 'text-amber-400'}`}>
                              {e.severity}
                            </span>
                          </div>
                          <div className="mt-0.5 text-slate-300">{e.message}</div>
                        </div>
                      ))}
                      {!selectedIncident.events?.length && <div className="text-xs text-slate-500">No events.</div>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'vps' && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
              <h3 className="mb-3 text-sm font-black text-slate-100">Service Registry</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="border-b border-slate-800 text-slate-400">
                    <tr>
                      <th className="py-2 pr-4 font-bold">ID</th>
                      <th className="py-2 pr-4 font-bold">Display Name</th>
                      <th className="py-2 pr-4 font-bold">Systemd Unit</th>
                      <th className="py-2 pr-4 font-bold">Check Type</th>
                      <th className="py-2 pr-4 font-bold">Health URL</th>
                      <th className="py-2 pr-4 font-bold">Actions</th>
                      <th className="py-2 pr-4 font-bold">Log Files</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((s) => (
                      <tr key={s.serviceId} className="border-b border-slate-800/50 last:border-0">
                        <td className="py-3 pr-4 font-mono text-slate-300">{s.serviceId}</td>
                        <td className="py-3 pr-4 text-slate-200">{s.displayName}</td>
                        <td className="py-3 pr-4 text-slate-400">{s.systemdUnit}</td>
                        <td className="py-3 pr-4 text-slate-400">{s.healthCheckType}</td>
                        <td className="py-3 pr-4 font-mono text-slate-500">{s.healthUrl || '-'}</td>
                        <td className="py-3 pr-4 text-slate-400">{s.permittedActions?.join(', ') || '-'}</td>
                        <td className="py-3 pr-4 text-slate-400">{s.logFiles?.join(', ') || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'settings' && config && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-4">
                <h3 className="mb-3 text-sm font-black text-cyan-300">How monitoring settings work</h3>
                <ul className="space-y-1.5 text-xs text-slate-300">
                  <li>The agent polls every service and the VPS metrics at the <span className="font-bold">poll interval</span>. Changes are pushed to the agent immediately (no restart needed).</li>
                  <li>A service becomes an incident after <span className="font-bold">failure threshold</span> consecutive failed polls. It resolves after <span className="font-bold">recovery threshold</span> consecutive healthy polls.</li>
                  <li>Alert emails are sent through the application&apos;s existing email service (the same sender/provider configured for the rest of the app). This page controls recipients and which alert types are sent; no SMTP setup is needed here.</li>
                  <li>The <span className="font-bold">alert cooldown</span> prevents repeat emails of the same type/service. Disk and memory alerts use their critical thresholds.</li>
                </ul>
              </div>

              <form onSubmit={handleSaveConfig} className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                <h3 className="text-sm font-black text-slate-100">Monitoring Settings</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {NUMERIC_FIELDS.map((field) => (
                    <div key={field.key}>
                      <label className="mb-1 block text-[11px] font-bold text-slate-300">{field.label}</label>
                      <input
                        type="number"
                        min={0}
                        value={config[field.key]}
                        onChange={(e) => updateConfigField(field.key, Number(e.target.value))}
                        className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-200 outline-none focus:border-cyan-500/40"
                      />
                      <p className="mt-1 text-[10px] font-semibold leading-snug text-slate-500">{field.help}</p>
                    </div>
                  ))}
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-300">Alert email sender</label>
                  <select
                    value={config.alertSenderProfileId || ''}
                    onChange={(e) => {
                      setConfig({ ...config, alertSenderProfileId: e.target.value || null });
                      configDirtyRef.current = true;
                      setConfigDirty(true);
                      setNotice(null);
                    }}
                    className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-200 outline-none focus:border-cyan-500/40"
                  >
                    <option value="">Application default email service</option>
                    {senderProfiles.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.name} — {profile.fromEmail}
                        {profile.hasSmtpPassword ? '' : ' (no SMTP password saved)'}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[10px] font-semibold text-slate-500">
                    Reuses a sender profile from Email Administration. Profiles with SMTP credentials deliver alert
                    emails directly; the application default provider is used when none is selected.
                  </p>
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-300">Alert recipients (comma-separated)</label>
                  <input
                    type="text"
                    value={config.alertRecipients?.join(', ') || ''}
                    onChange={(e) => {
                      setConfig({ ...config, alertRecipients: e.target.value.split(',').map((v) => v.trim()).filter(Boolean) });
                      configDirtyRef.current = true;
                      setConfigDirty(true);
                      setNotice(null);
                    }}
                    placeholder="ops@example.com, oncall@example.com"
                    className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-200 outline-none focus:border-cyan-500/40 placeholder:text-slate-600"
                  />
                  <p className="mt-1 text-[10px] font-semibold text-slate-500">
                    Recipients are stored in the database and emailed through the sender selected above.
                  </p>
                </div>

                <div>
                  <span className="mb-2 block text-[11px] font-bold text-slate-300">Enabled alert types</span>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {ALERT_TYPES.map((type) => {
                      const checked = config.enabledAlertTypes?.includes(type.value) || false;
                      return (
                        <label
                          key={type.value}
                          className={`flex cursor-pointer items-start gap-2 rounded-xl border p-3 transition ${
                            checked ? 'border-cyan-500/30 bg-cyan-500/5' : 'border-slate-800 bg-slate-900/60 hover:border-slate-700'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleAlertType(type.value)}
                            className="mt-0.5 h-3.5 w-3.5 accent-cyan-500"
                          />
                          <span>
                            <span className="block text-[11px] font-bold text-slate-200">{type.label}</span>
                            <span className="block text-[10px] font-semibold leading-snug text-slate-500">{type.help}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="submit"
                    className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-4 py-2 text-xs font-bold text-cyan-400 hover:bg-cyan-500/20"
                  >
                    Save Settings
                  </button>
                  <button
                    type="button"
                    onClick={handleTestAlert}
                    className="flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-xs font-bold text-slate-200 hover:border-cyan-500/40 hover:text-cyan-400"
                  >
                    <Bell size={14} /> Test Alert
                  </button>
                  {configDirty && <span className="text-[11px] font-bold text-amber-400">Unsaved changes</span>}
                </div>
              </form>

              <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                <h3 className="mb-3 text-sm font-black text-slate-100">How to test these settings</h3>
                <ol className="list-decimal space-y-2 pl-5 text-xs text-slate-300">
                  <li>Enter at least one recipient email and press <span className="font-bold">Save Settings</span>. Settings are pushed to the agent immediately.</li>
                  <li>Press <span className="font-bold">Test Alert</span>. The backend sends a test email immediately through the email service to every recipient and reports how many were delivered or failed.</li>
                  <li>To test incidents, stop a service that is <span className="font-bold">not serving this dashboard</span> (for example another environment) from the Services tab. After the failure threshold it opens an ACTIVE incident and sends BACKEND_DOWN.</li>
                  <li>Start the service again. After the recovery threshold of healthy polls the incident becomes RESOLVED and SERVICE_RECOVERY is sent.</li>
                  <li>If the test fails, check the email provider/sender configuration in Email Administration; recipients and alert types are working but delivery is not.</li>
                </ol>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
